/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import { MED_DATETIME_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Typography2 } from '@coursera/cds-core';

import { SubmissionStatusEnum } from 'bundles/grading/api/types';
import LoadingSkeleton from 'bundles/grading/components/common/LoadingSkeleton';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import { AutoSaveContext } from 'bundles/grading/components/staff-graded-assignments/auto-save/utils';
import UserChangeLogDrawer from 'bundles/grading/components/staff-graded-assignments/top-bar/UserChangeLogDrawer';

import _t from 'i18n!nls/grading';

const styles = {
  container: css`
    color: var(--cds-color-neutral-primary-weak);
    margin: 0 var(--cds-spacing-400);
  `,
  changeLogLink: css`
    text-decoration: underline;
    cursor: pointer;
  `,
  lazyLoad: css`
    margin: 0 var(--cds-spacing-400);
  `,
};

// Component for SGA top bar Last Saved Status display
const LastSavedStatus = () => {
  const { submissionData, mutations, isPrivateSession } = useContext(SGAPageContext);
  const { autoSaveTaskStates } = useContext(AutoSaveContext);
  const [isChangeLogDrawerOpen, setChangeLogDrawerOpen] = useState(false);

  const { submitter, lastGradingAction } = submissionData.data || {};

  const toggleDrawer = () => {
    setChangeLogDrawerOpen((prevState) => !prevState);
  };

  const currentAutoSaveTask = autoSaveTaskStates[submitter?.submitterId || ''];

  if (submissionData.loading) {
    return <LoadingSkeleton width="250px" height="30px" cssOverride={styles.lazyLoad} />;
  }

  let learnerStatus;
  let savedBy;
  if (lastGradingAction) {
    const date = formatDateTimeDisplay(parseInt(lastGradingAction.actionTime.seconds, 10) * 1000, MED_DATETIME_DISPLAY);
    const [firstName, lastName] = (lastGradingAction.fullName || '').split(' ');
    const lastInitial = lastName && lastName.length > 0 && lastName.slice(0, 1);
    const name = lastInitial == null ? `${firstName}` : `${firstName} ${lastInitial}.`;
    savedBy = _t('Last saved by #{name} on #{date}', { name, date });
  } else if (submitter?.submissionStatus === SubmissionStatusEnum.NotYetSubmitted) {
    learnerStatus = _t('Learner started assignment');
  } else if (submitter?.submissionStatus === SubmissionStatusEnum.ReadyForGrading) {
    learnerStatus = _t('Learner submitted assignment');
  }

  let currentStatus;
  if (currentAutoSaveTask?.isSaving || mutations?.updateGradeVisibilityStatus?.loading) {
    currentStatus = _t('Saving...');
  } else if (currentAutoSaveTask?.isSubmitting) {
    currentStatus = _t('Submitting to gradebook...');
  }

  if (!savedBy && !learnerStatus && !currentStatus) {
    return null;
  }

  return isPrivateSession ? (
    <div css={styles.container}>
      <Typography2
        data-testid="last-saved-status"
        className="pii-hide"
        variant="bodySecondary"
        component="span"
        color="inherit"
        css={styles.changeLogLink}
        onClick={toggleDrawer}
      >
        {currentStatus}
        {!currentStatus && savedBy}
        {!currentStatus && learnerStatus}
      </Typography2>
      <UserChangeLogDrawer isOpen={isChangeLogDrawerOpen} onClose={toggleDrawer} />
    </div>
  ) : (
    <div css={styles.container}>
      <Typography2
        data-testid="last-saved-status"
        className="pii-hide"
        variant="bodySecondary"
        component="span"
        color="inherit"
      >
        {currentStatus}
        {!currentStatus && savedBy}
        {!currentStatus && learnerStatus}
      </Typography2>
    </div>
  );
};

export default LastSavedStatus;
